import isNil from 'lodash/isNil';

import { InvoiceStatus, Posting, PostingStatus, ReconciliationReasonCode } from '../types';

type SettlementPosting = Pick<Posting, 'status' | 'transactionId' | 'invoiceId'>;

/**
 * A payment settled against an early draft is held as inert DRAFT postings until the draft is posted
 * (decision D16, docs/specs/draft-invoices/00_Cadrage_Draft_Invoices.md). These postings are the operational state;
 * when P2.10 holds the allocation in the journal alone, this predicate is the only place to change.
 */
export const isAwaitingPostingSettlement = (posting: SettlementPosting): boolean =>
  posting.status === PostingStatus.DRAFT && !isNil(posting.transactionId) && !isNil(posting.invoiceId);

export const hasSettlementAwaitingPosting = (postings: SettlementPosting[] | null | undefined): boolean =>
  (postings ?? []).some(isAwaitingPostingSettlement);

type SettledDocument = { id: string; status?: InvoiceStatus | null };

export const getDraftInvoiceIds = (invoices: SettledDocument[]): string[] =>
  invoices.reduce<string[]>(
    (acc, invoice) => (invoice.status === InvoiceStatus.DRAFT ? [...acc, invoice.id] : acc),
    []
  );

export const withAwaitingPostingReason = (
  reasonCodes: ReconciliationReasonCode[],
  invoiceIds: string[],
  invoices: SettledDocument[]
): ReconciliationReasonCode[] => {
  const settlesADraft = getDraftInvoiceIds(invoices).some((invoiceId) => invoiceIds.includes(invoiceId));
  if (!settlesADraft || reasonCodes.includes(ReconciliationReasonCode.AWAITING_POSTING)) {
    return reasonCodes;
  }
  return [...reasonCodes, ReconciliationReasonCode.AWAITING_POSTING];
};
